import React, { useState } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { Link } from 'react-router-dom';
import { 
  Sparkles, 
  Calendar, 
  Clock, 
  MapPin, 
  User, 
  Mail, 
  Phone, 
  Home,
  CheckCircle,
  ArrowRight,
  Star,
  Award
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WebinarRegistrationPage = () => {
  usePageTitle('Selenite Healing Mastery Webinar Registration');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    experience_level: '',
    interests: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [registrationId, setRegistrationId] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/webinar/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Registration failed');
      }

      const data = await response.json();
      setRegistrationId(data.id.slice(0, 8).toUpperCase());
      setIsRegistered(true);
      toast.success('Registration successful! Check your email for confirmation.');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isRegistered) {
    return (
      <div className="min-h-screen bg-[#faf8f5] py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-[#00C853] to-[#00B4D8] rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-serif text-4xl text-[#1e1919] mb-4">Registration Complete!</h1>
          <p className="text-lg text-[#61525a] mb-8">
            Thank you for registering for the Selenite Healing Mastery webinar. 
            A confirmation email has been sent to your inbox.
          </p>
          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <p className="text-sm text-[#7A7A9A] uppercase tracking-wider mb-2">Your Registration ID</p>
            <p className="text-3xl font-mono font-bold text-[#9D4EDD]" data-testid="registration-id">{registrationId}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://gemstoneminerals.com/romona-nicole-schmidt-gemstone-healing-expert/"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
              data-testid="back-to-speaker-btn"
            >
              Learn More About Romona Nicole Schmidt
            </a>
            <Link
              to="/shop?category=selenite"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#9D4EDD] text-[#9D4EDD] px-6 py-3 rounded-xl font-semibold hover:bg-[#9D4EDD] hover:text-white transition-all duration-300"
              data-testid="shop-selenite-btn"
            >
              Shop Selenite Crystals
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Hero */}
      <div className="relative py-16 px-6 bg-[#1e1919] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, #9D4EDD 0%, transparent 50%),
                             radial-gradient(circle at 80% 50%, #c9a96e 0%, transparent 50%)`
          }} />
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-[#c9a96e]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#c9a96e] font-medium">
              Exclusive Live Event
            </span>
          </div>
          <h1 className="font-serif text-3xl md:text-5xl text-white mb-4">
            Selenite Healing Mastery
          </h1>
          <p className="text-lg text-[#bbb5ae] mb-6">
            A Transformative Live Webinar with Romona Nicole Schmidt
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-white/80">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#c9a96e]" />
              <span>July 2026</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#c9a96e]" />
              <span>Live Online Event</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-[#c9a96e]" />
              <span>Certificate Included</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Event Details Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Price Card */}
              <div className="bg-gradient-to-br from-[#9D4EDD] to-[#FF6B9D] rounded-2xl p-8 text-white shadow-xl">
                <p className="text-sm uppercase tracking-wider opacity-80 mb-2">Investment</p>
                <p className="text-4xl font-serif mb-4">$800</p>
                <p className="text-sm opacity-80">Per person</p>
                <div className="mt-6 pt-6 border-t border-white/20">
                  <p className="text-sm font-semibold mb-3">Includes:</p>
                  <ul className="space-y-2">
                    {[
                      'Live 3-hour webinar',
                      'Q&A with Romona',
                      'Digital workbook',
                      'Certificate of completion',
                      'Recording access (30 days)'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Speaker Card */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src="https://customer-assets.emergentagent.com/job_12c5979f-07d4-4fbe-88fc-c2d666777007/artifacts/3xo7hul1_Facetune_16-11-2024-19-08-45.jpeg"
                    alt="Romona Nicole Schmidt"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="font-serif text-lg text-[#1e1919]">Romona Nicole Schmidt</h3>
                    <p className="text-sm text-[#c9a96e]">Guest Educator</p>
                  </div>
                </div>
                <p className="text-sm text-[#61525a] mb-4">
                  Vibrational wellness expert based in Sedona, Arizona with 10+ years of experience.
                </p>
                <Link
                  to="/kholey"
                  className="text-sm text-[#9D4EDD] font-semibold hover:underline flex items-center gap-1"
                  data-testid="learn-more-speaker-btn"
                >
                  Learn more about Romona
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* What You'll Learn */}
              <div className="bg-[#F8F5FF] rounded-2xl p-6">
                <h3 className="font-serif text-lg text-[#1e1919] mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#FFB800]" />
                  What You'll Learn
                </h3>
                <ul className="space-y-3">
                  {[
                    'Selenite crystal properties & energy',
                    'Cleansing & charging techniques',
                    'Space purification methods',
                    'Meditation with Selenite',
                    'Creating crystal grids',
                    'Ethical sourcing practices'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[#4A4A6A]">
                      <CheckCircle className="w-4 h-4 text-[#00C853] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Registration Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="font-serif text-2xl text-[#1e1919] mb-2">Register for the Webinar</h2>
              <p className="text-[#61525a] mb-8">Complete the form below to secure your spot.</p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="registration-form">
                {/* Personal Information */}
                <div>
                  <h3 className="font-semibold text-[#1e1919] mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-[#9D4EDD]" />
                    Personal Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#4A4A6A] mb-2">First Name *</label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                        placeholder="Enter your first name"
                        data-testid="first-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Last Name *</label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                        placeholder="Enter your last name"
                        data-testid="last-name-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="font-semibold text-[#1e1919] mb-4 flex items-center gap-2">
                    <Mail className="w-5 h-5 text-[#9D4EDD]" />
                    Contact Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                        placeholder="your@email.com"
                        data-testid="email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                        placeholder="+1 (555) 000-0000"
                        data-testid="phone-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h3 className="font-semibold text-[#1e1919] mb-4 flex items-center gap-2">
                    <Home className="w-5 h-5 text-[#9D4EDD]" />
                    Mailing Address
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Street Address *</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                        placeholder="123 Main Street"
                        data-testid="address-input"
                      />
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#4A4A6A] mb-2">City *</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                          placeholder="City"
                          data-testid="city-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#4A4A6A] mb-2">State *</label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                          placeholder="State"
                          data-testid="state-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#4A4A6A] mb-2">ZIP Code *</label>
                        <input
                          type="text"
                          name="zip_code"
                          value={formData.zip_code}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                          placeholder="12345"
                          data-testid="zip-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Country</label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none bg-[#faf8f5]"
                        data-testid="country-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Experience Level */}
                <div>
                  <h3 className="font-semibold text-[#1e1919] mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-[#9D4EDD]" />
                    Your Experience
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Crystal Healing Experience Level *</label>
                    <select
                      name="experience_level"
                      value={formData.experience_level}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                      data-testid="experience-select"
                    >
                      <option value="">Select your experience level</option>
                      <option value="beginner">Beginner - New to crystal healing</option>
                      <option value="intermediate">Intermediate - Some experience</option>
                      <option value="advanced">Advanced - Experienced practitioner</option>
                    </select>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-[#4A4A6A] mb-2">What interests you most about this webinar?</label>
                    <textarea
                      name="interests"
                      value={formData.interests}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none resize-none"
                      placeholder="Tell us what you hope to learn..."
                      data-testid="interests-textarea"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white py-4 rounded-xl font-semibold text-lg hover:shadow-[0_8px_25px_rgba(157,78,221,0.4)] transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Complete Registration - $800
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                  <p className="text-xs text-center text-[#7A7A9A] mt-4">
                    By registering, you agree to our terms and conditions. 
                    Payment will be processed after form submission.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebinarRegistrationPage;
